import { create } from 'zustand';
import type { Landmark } from '../types/landmark';
import type { Business } from '../types/business';
import type { AntwerpLandmark, AntwerpLandmarkCategory } from '../types/antwerp-landmark';
import { ANTWERP_CATEGORIES } from '../types/antwerp-landmark';
import type { CustomPOI } from '../types/custom-poi';
import type { SidePanelTab } from '../components/LandmarkPanelTabs';
import {
  loadOverlayState,
  saveOverlayState,
  type HistoricalOverlayId,
  type OverlayStates,
} from '../lib/historical-overlays';
import { clampYear, overlayStatesForYear } from '../lib/time-overlay-mapping';
import { loadBaseLayer, saveBaseLayer, type BaseLayerId } from '../lib/base-layers';
import {
  deleteCustomPOIImage,
  putCustomPOIImage,
} from '../lib/custom-poi-images';
import {
  deletePOI as apiDeletePOI,
  fetchAdminPaperFrame,
  fetchAllPOIs,
  fetchLandmarkOverrides,
  fetchPublicPaperFrame,
  fetchSelectionIds,
  patchLandmarkOverride,
  replaceSelection,
  saveAdminPaperFrame,
  savePOI as apiSavePOI,
  whoami,
  type LandmarkOverride,
  type ServerPaperFrame,
} from '../api/artist-pois';

const HERITAGE_VISIBILITY_KEY = 'mapLens.heritageVisible.v2';
const ANTWERP_VISIBILITY_KEY = 'mapLens.antwerpHistoricalVisible.v2';
const ANTWERP_HIDDEN_CATEGORIES_KEY = 'mapLens.antwerpHiddenCategories.v1';
const ARTIST_SELECTION_KEY = 'mapLens.artistSelection.v1';
const ARTIST_SELECTION_ONLY_KEY = 'mapLens.artistSelectionOnly.v1';
const CUSTOM_POIS_KEY = 'mapLens.customPOIs.v1';
const PAPER_FRAME_VISIBILITY_KEY = 'mapLens.paperFrameVisible.v1';
const PAPER_FRAME_GEOMETRY_KEY = 'mapLens.paperFrame.v1';

interface PaperFrameGeometry {
  centerLng: number;
  centerLat: number;
  shortSideMeters: number;
  rotationDeg: number;
  opacity: number;
  borderVisible: boolean;
}

// Defaults sit over the Antwerp old town with the long axis north-south at
// roughly ~1.5 km × ~2.1 km — a sensible artist briefing area.
const DEFAULT_PAPER_FRAME: PaperFrameGeometry = {
  centerLng: 4.402,
  centerLat: 51.221,
  shortSideMeters: 1500,
  rotationDeg: 0,
  opacity: 0.08,
  borderVisible: true,
};

function loadPaperFrame(): PaperFrameGeometry {
  if (typeof window === 'undefined') return DEFAULT_PAPER_FRAME;
  try {
    const raw = window.localStorage.getItem(PAPER_FRAME_GEOMETRY_KEY);
    if (!raw) return DEFAULT_PAPER_FRAME;
    const parsed = JSON.parse(raw) as Partial<PaperFrameGeometry>;
    return {
      centerLng:
        typeof parsed.centerLng === 'number' ? parsed.centerLng : DEFAULT_PAPER_FRAME.centerLng,
      centerLat:
        typeof parsed.centerLat === 'number' ? parsed.centerLat : DEFAULT_PAPER_FRAME.centerLat,
      shortSideMeters:
        typeof parsed.shortSideMeters === 'number' && parsed.shortSideMeters > 0
          ? parsed.shortSideMeters
          : DEFAULT_PAPER_FRAME.shortSideMeters,
      rotationDeg:
        typeof parsed.rotationDeg === 'number' ? parsed.rotationDeg : DEFAULT_PAPER_FRAME.rotationDeg,
      opacity:
        typeof parsed.opacity === 'number' && parsed.opacity >= 0 && parsed.opacity <= 1
          ? parsed.opacity
          : DEFAULT_PAPER_FRAME.opacity,
      borderVisible:
        typeof parsed.borderVisible === 'boolean'
          ? parsed.borderVisible
          : DEFAULT_PAPER_FRAME.borderVisible,
    };
  } catch {
    return DEFAULT_PAPER_FRAME;
  }
}

function savePaperFrame(geometry: PaperFrameGeometry): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PAPER_FRAME_GEOMETRY_KEY, JSON.stringify(geometry));
  } catch {
    /* ignore */
  }
}

// Debounce admin drag → server save so each pointer move doesn't fire a PUT.
let paperFrameSyncTimer: ReturnType<typeof setTimeout> | null = null;
function schedulePaperFrameServerSync(): void {
  if (typeof window === 'undefined') return;
  const state = useLandmarkStore.getState();
  if (!state.isAdmin || state.paperFrameLocked) return;
  if (paperFrameSyncTimer !== null) clearTimeout(paperFrameSyncTimer);
  paperFrameSyncTimer = setTimeout(() => {
    paperFrameSyncTimer = null;
    const s = useLandmarkStore.getState();
    if (!s.isAdmin || s.paperFrameLocked) return;
    void s.publishPaperFrame(false).catch(() => {
      // Swallow — the next dragend or lock attempt will retry; we already
      // wrote to localStorage so the admin keeps their position locally.
    });
  }, 400);
}

function loadCustomPOIs(): Record<string, CustomPOI> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(CUSTOM_POIS_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed as Record<string, CustomPOI>;
  } catch {
    return {};
  }
}

function saveCustomPOIs(map: Record<string, CustomPOI>): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CUSTOM_POIS_KEY, JSON.stringify(map));
  } catch {
    /* ignore — quota errors surface as save failures upstream */
  }
}

/**
 * Seed list of landmark ids for "Artist selection". On the very first
 * load (when no value has been stored yet) we copy this list into
 * localStorage; from then on the user owns the set fully (additions and
 * removals via the side panel persist).
 */
export const ARTIST_SELECTION_SEED: ReadonlyArray<string> = [
  'lange-nieuwstraat-81',
  'rubenshuis',
  'antwerp-bourse-1531',
  'oude-beurs',
  'boerentoren-1932',
  'cathedral-of-our-lady',
  'grote-markt',
  'stadhuis',
  'brabo-antigoon-statue',
  'sint-jacobskerk',
  'sint-pauluskerk',
  'het-steen',
  'vleeshuis',
  'kmska',
  'bourlaschouwburg',
  'vlaaikensgang',
  'den-botaniek',
  'nationalestraat',
  'plantin-moretus',
  'kloosterstraat-antiek',
  'sint-andrieskerk',
  'mas',
  'pelikaanstraat-diamonds',
  'meir-marsh-origin',
];

function loadArtistSelection(): Set<string> {
  if (typeof window === 'undefined') return new Set(ARTIST_SELECTION_SEED);
  try {
    const raw = window.localStorage.getItem(ARTIST_SELECTION_KEY);
    if (raw === null) {
      // First run: seed and persist.
      const seeded = new Set<string>(ARTIST_SELECTION_SEED);
      window.localStorage.setItem(ARTIST_SELECTION_KEY, JSON.stringify([...seeded]));
      return seeded;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set(ARTIST_SELECTION_SEED);
    return new Set(parsed.filter((v): v is string => typeof v === 'string'));
  } catch {
    return new Set(ARTIST_SELECTION_SEED);
  }
}

function saveArtistSelection(set: Set<string>): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(ARTIST_SELECTION_KEY, JSON.stringify([...set]));
  } catch {
    /* ignore */
  }
}

function loadHiddenCategories(): Set<AntwerpLandmarkCategory> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(ANTWERP_HIDDEN_CATEGORIES_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    const valid = new Set(ANTWERP_CATEGORIES);
    return new Set(parsed.filter((v): v is AntwerpLandmarkCategory => typeof v === 'string' && valid.has(v as AntwerpLandmarkCategory)));
  } catch {
    return new Set();
  }
}

function saveHiddenCategories(set: Set<AntwerpLandmarkCategory>): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(ANTWERP_HIDDEN_CATEGORIES_KEY, JSON.stringify([...set]));
  } catch {
    /* ignore */
  }
}
function loadBool(key: string, fallback: boolean): boolean {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? fallback : raw === 'true';
  } catch {
    return fallback;
  }
}
function saveBool(key: string, value: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    /* ignore */
  }
}

/**
 * Selection state — at most one of `selectedLandmark` or `selectedBusiness`
 * is non-null at a time. The setters enforce the invariant.
 */
interface SelectionStore {
  selectedLandmark: Landmark | null;
  selectedBusiness: Business | null;
  selectedAntwerpLandmark: AntwerpLandmark | null;
  selectLandmark: (landmark: Landmark | null) => void;
  selectBusiness: (business: Business | null) => void;
  selectAntwerpLandmark: (al: AntwerpLandmark | null) => void;
  clearSelection: () => void;
  /** Layer visibility — Onroerend Erfgoed pins (the 80k cluster layer). */
  heritageVisible: boolean;
  toggleHeritage: () => void;
  /** Layer visibility — curated Antwerp historical landmarks (the artist-brief overlay). */
  antwerpHistoricalVisible: boolean;
  toggleAntwerpHistorical: () => void;
  /** Categories the user has hidden inside the Historical Antwerp layer. */
  antwerpHiddenCategories: Set<AntwerpLandmarkCategory>;
  toggleAntwerpCategory: (category: AntwerpLandmarkCategory) => void;
  showAllAntwerpCategories: () => void;
  hideAllAntwerpCategories: () => void;
  /** Ids the user has marked as "artist selection" (the curated short-list for the illustrator). */
  artistSelection: Set<string>;
  toggleArtistSelectionMember: (id: string) => void;
  /** When true, the map only shows artist-selection landmarks. */
  artistSelectionOnly: boolean;
  toggleArtistSelectionOnly: () => void;

  /**
   * Paper-size frame for briefing the artist. Locked to A-series aspect (1:√2).
   *
   * Geometry + lock are stored on the server (singleton row). When locked, the
   * frame is published — everyone sees it but no one can edit. When unlocked
   * only the admin sees it; the admin's drag edits push to the server (so the
   * latest position is captured if they re-lock). The opacity, border, and the
   * per-user visibility toggle remain in localStorage so each viewer can pick
   * their own transparency without affecting others.
   */
  paperFrameVisible: boolean;
  togglePaperFrameVisible: () => void;
  paperFrameCenter: [number, number];
  setPaperFrameCenter: (center: [number, number]) => void;
  paperFrameShortSideMeters: number;
  setPaperFrameShortSide: (meters: number) => void;
  paperFrameRotation: number;
  setPaperFrameRotation: (degrees: number) => void;
  paperFrameOpacity: number;
  setPaperFrameOpacity: (opacity: number) => void;
  paperFrameBorderVisible: boolean;
  togglePaperFrameBorder: () => void;
  resetPaperFrame: () => void;
  /** True when the frame is locked on the server (published, read-only). */
  paperFrameLocked: boolean;
  /** True once we've completed at least one server fetch (admin or public). */
  paperFrameServerLoaded: boolean;
  /** True when a server PUT is in flight (admin only). */
  paperFrameSaving: boolean;
  /** Push the current geometry + a given lock state to the server. Admin only. */
  publishPaperFrame: (locked: boolean) => Promise<void>;

  /** User-created points of interest, keyed by id. Persists in localStorage. */
  customPOIs: Record<string, CustomPOI>;
  /** Hero-image dataURLs keyed by POI id; cache populated from IndexedDB on boot. */
  customPOIImages: Record<string, string>;
  /** Map right-click context-menu state. null = closed. */
  contextMenu: { lng: number; lat: number; screenX: number; screenY: number } | null;
  openContextMenu: (menu: { lng: number; lat: number; screenX: number; screenY: number }) => void;
  closeContextMenu: () => void;
  /** Form-modal state. null = closed. */
  editingPOI:
    | { mode: 'new'; coordinates: [number, number] }
    | { mode: 'edit'; poi: CustomPOI }
    | null;
  openNewPOIForm: (coordinates: [number, number]) => void;
  openEditPOIForm: (poi: CustomPOI) => void;
  closePOIForm: () => void;
  /** Persist a POI (new or updated) + optional new image dataURL. */
  saveCustomPOI: (poi: CustomPOI, imageDataURL: string | null) => Promise<void>;
  /** Remove a POI and its image. */
  deleteCustomPOI: (id: string) => Promise<void>;
  /** Replace the image cache (used after IDB hydration on boot). */
  setCustomPOIImages: (images: Record<string, string>) => void;
  /** Authenticated user's email (from Cloudflare Access), null when signed out. */
  currentUserEmail: string | null;
  isAdmin: boolean;
  /** Re-check the Access cookie. Called on boot and after sign-in returns. */
  refreshAdminFlag: () => Promise<void>;
  /** Pull server state into the store. Idempotent. */
  hydrateFromServer: () => Promise<void>;
  /** Server-stored per-landmark edits keyed by landmark id. */
  landmarkOverrides: Record<string, LandmarkOverride>;
  /**
   * Save (or revert) one or more fields for a landmark. Pass null as the
   * value of a field to revert it to baseline. Optimistic + server sync.
   */
  saveLandmarkOverride: (id: string, fields: Record<string, string | null>) => Promise<void>;
  activeTab: SidePanelTab;
  setActiveTab: (tab: SidePanelTab) => void;
  /**
   * When set, the map should fly to these coordinates and clear the flag.
   * Used by the search bar to fly to a result on selection.
   */
  flyToTarget: [number, number] | null;
  /** Optional zoom override for the next flyTo. `null` = use map default. */
  flyToZoom: number | null;
  flyTo: (coordinates: [number, number], zoom?: number) => void;
  consumeFlyToTarget: () => void;
  /** Active base map style; persists in localStorage. */
  baseLayer: BaseLayerId;
  setBaseLayer: (id: BaseLayerId) => void;
  /** Visibility + opacity per historical overlay; persists in localStorage. */
  overlays: OverlayStates;
  toggleOverlay: (id: HistoricalOverlayId) => void;
  setOverlayOpacity: (id: HistoricalOverlayId, opacity: number) => void;
  /** Active year on the time-slider, or null when the slider hasn't been touched. */
  currentYear: number | null;
  /**
   * Set the current year. Pass `null` to "release" the slider (overlays return
   * to whatever the layer panel had). Pass a number to drive overlays via the
   * year-to-opacity mapping.
   */
  setYear: (year: number | null) => void;
}

export const useLandmarkStore = create<SelectionStore>((set) => ({
  selectedLandmark: null,
  selectedBusiness: null,
  selectedAntwerpLandmark: null,
  activeTab: 'about',
  flyToTarget: null,
  flyToZoom: null,
  selectLandmark: (landmark) =>
    set((state) => ({
      selectedLandmark: landmark,
      selectedBusiness: null,
      selectedAntwerpLandmark: null,
      activeTab:
        landmark === null || landmark.id === state.selectedLandmark?.id ? state.activeTab : 'about',
    })),
  selectBusiness: (business) =>
    set({ selectedLandmark: null, selectedBusiness: business, selectedAntwerpLandmark: null, activeTab: 'about' }),
  selectAntwerpLandmark: (al) =>
    set({ selectedLandmark: null, selectedBusiness: null, selectedAntwerpLandmark: al }),
  clearSelection: () =>
    set({ selectedLandmark: null, selectedBusiness: null, selectedAntwerpLandmark: null }),
  heritageVisible: loadBool(HERITAGE_VISIBILITY_KEY, false),
  toggleHeritage: () =>
    set((state) => {
      const next = !state.heritageVisible;
      saveBool(HERITAGE_VISIBILITY_KEY, next);
      return { heritageVisible: next };
    }),
  antwerpHistoricalVisible: loadBool(ANTWERP_VISIBILITY_KEY, true),
  toggleAntwerpHistorical: () =>
    set((state) => {
      const next = !state.antwerpHistoricalVisible;
      saveBool(ANTWERP_VISIBILITY_KEY, next);
      return { antwerpHistoricalVisible: next };
    }),
  antwerpHiddenCategories: loadHiddenCategories(),
  toggleAntwerpCategory: (category) =>
    set((state) => {
      const next = new Set(state.antwerpHiddenCategories);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      saveHiddenCategories(next);
      return { antwerpHiddenCategories: next };
    }),
  showAllAntwerpCategories: () =>
    set(() => {
      const next = new Set<AntwerpLandmarkCategory>();
      saveHiddenCategories(next);
      return { antwerpHiddenCategories: next };
    }),
  hideAllAntwerpCategories: () =>
    set(() => {
      const next = new Set<AntwerpLandmarkCategory>(ANTWERP_CATEGORIES);
      saveHiddenCategories(next);
      return { antwerpHiddenCategories: next };
    }),
  artistSelection: loadArtistSelection(),
  toggleArtistSelectionMember: (id) => {
    // Optimistic local update + server PUT. On failure we revert.
    const state = useLandmarkStore.getState();
    if (!state.isAdmin) return;
    const prev = state.artistSelection;
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    saveArtistSelection(next);
    set({ artistSelection: next });
    replaceSelection([...next]).catch(() => {
      // Roll back on server failure.
      saveArtistSelection(prev);
      set({ artistSelection: prev });
    });
  },
  artistSelectionOnly: loadBool(ARTIST_SELECTION_ONLY_KEY, false),
  toggleArtistSelectionOnly: () =>
    set((state) => {
      const next = !state.artistSelectionOnly;
      saveBool(ARTIST_SELECTION_ONLY_KEY, next);
      return { artistSelectionOnly: next };
    }),

  paperFrameVisible: loadBool(PAPER_FRAME_VISIBILITY_KEY, true),
  togglePaperFrameVisible: () =>
    set((state) => {
      const next = !state.paperFrameVisible;
      saveBool(PAPER_FRAME_VISIBILITY_KEY, next);
      return { paperFrameVisible: next };
    }),
  paperFrameCenter: (() => {
    const g = loadPaperFrame();
    return [g.centerLng, g.centerLat] as [number, number];
  })(),
  setPaperFrameCenter: (center) => {
    const state = useLandmarkStore.getState();
    if (state.paperFrameLocked || !state.isAdmin) return;
    savePaperFrame({
      centerLng: center[0],
      centerLat: center[1],
      shortSideMeters: state.paperFrameShortSideMeters,
      rotationDeg: state.paperFrameRotation,
      opacity: state.paperFrameOpacity,
      borderVisible: state.paperFrameBorderVisible,
    });
    set({ paperFrameCenter: center });
    schedulePaperFrameServerSync();
  },
  paperFrameShortSideMeters: loadPaperFrame().shortSideMeters,
  setPaperFrameShortSide: (meters) => {
    const state = useLandmarkStore.getState();
    if (state.paperFrameLocked || !state.isAdmin) return;
    const clamped = Math.max(50, meters);
    savePaperFrame({
      centerLng: state.paperFrameCenter[0],
      centerLat: state.paperFrameCenter[1],
      shortSideMeters: clamped,
      rotationDeg: state.paperFrameRotation,
      opacity: state.paperFrameOpacity,
      borderVisible: state.paperFrameBorderVisible,
    });
    set({ paperFrameShortSideMeters: clamped });
    schedulePaperFrameServerSync();
  },
  paperFrameRotation: loadPaperFrame().rotationDeg,
  setPaperFrameRotation: (degrees) => {
    const state = useLandmarkStore.getState();
    if (state.paperFrameLocked || !state.isAdmin) return;
    savePaperFrame({
      centerLng: state.paperFrameCenter[0],
      centerLat: state.paperFrameCenter[1],
      shortSideMeters: state.paperFrameShortSideMeters,
      rotationDeg: degrees,
      opacity: state.paperFrameOpacity,
      borderVisible: state.paperFrameBorderVisible,
    });
    set({ paperFrameRotation: degrees });
    schedulePaperFrameServerSync();
  },
  paperFrameOpacity: loadPaperFrame().opacity,
  setPaperFrameOpacity: (opacity) =>
    set((state) => {
      const clamped = Math.max(0, Math.min(1, opacity));
      savePaperFrame({
        centerLng: state.paperFrameCenter[0],
        centerLat: state.paperFrameCenter[1],
        shortSideMeters: state.paperFrameShortSideMeters,
        rotationDeg: state.paperFrameRotation,
        opacity: clamped,
        borderVisible: state.paperFrameBorderVisible,
      });
      return { paperFrameOpacity: clamped };
    }),
  paperFrameBorderVisible: loadPaperFrame().borderVisible,
  togglePaperFrameBorder: () =>
    set((state) => {
      const next = !state.paperFrameBorderVisible;
      savePaperFrame({
        centerLng: state.paperFrameCenter[0],
        centerLat: state.paperFrameCenter[1],
        shortSideMeters: state.paperFrameShortSideMeters,
        rotationDeg: state.paperFrameRotation,
        opacity: state.paperFrameOpacity,
        borderVisible: next,
      });
      return { paperFrameBorderVisible: next };
    }),
  resetPaperFrame: () => {
    const state = useLandmarkStore.getState();
    if (state.paperFrameLocked || !state.isAdmin) return;
    savePaperFrame(DEFAULT_PAPER_FRAME);
    set({
      paperFrameCenter: [DEFAULT_PAPER_FRAME.centerLng, DEFAULT_PAPER_FRAME.centerLat],
      paperFrameShortSideMeters: DEFAULT_PAPER_FRAME.shortSideMeters,
      paperFrameRotation: DEFAULT_PAPER_FRAME.rotationDeg,
      paperFrameOpacity: DEFAULT_PAPER_FRAME.opacity,
      paperFrameBorderVisible: DEFAULT_PAPER_FRAME.borderVisible,
    });
    schedulePaperFrameServerSync();
  },
  paperFrameLocked: false,
  paperFrameServerLoaded: false,
  paperFrameSaving: false,
  publishPaperFrame: async (locked) => {
    const state = useLandmarkStore.getState();
    if (!state.isAdmin) throw new Error('You need to sign in to update the artist frame.');
    set({ paperFrameSaving: true });
    try {
      const result = await saveAdminPaperFrame({
        centerLng: state.paperFrameCenter[0],
        centerLat: state.paperFrameCenter[1],
        shortSideMeters: state.paperFrameShortSideMeters,
        rotationDeg: state.paperFrameRotation,
        locked,
      });
      set({
        paperFrameLocked: result.locked,
        paperFrameCenter: [result.centerLng, result.centerLat],
        paperFrameShortSideMeters: result.shortSideMeters,
        paperFrameRotation: result.rotationDeg,
        paperFrameSaving: false,
      });
    } catch (err) {
      set({ paperFrameSaving: false });
      throw err;
    }
  },

  customPOIs: loadCustomPOIs(),
  customPOIImages: {},
  contextMenu: null,
  openContextMenu: (menu) => set({ contextMenu: menu }),
  closeContextMenu: () => set({ contextMenu: null }),
  editingPOI: null,
  openNewPOIForm: (coordinates) =>
    set({ editingPOI: { mode: 'new', coordinates }, contextMenu: null }),
  openEditPOIForm: (poi) => set({ editingPOI: { mode: 'edit', poi } }),
  closePOIForm: () => set({ editingPOI: null }),
  setCustomPOIImages: (images) => set({ customPOIImages: images }),
  currentUserEmail: null,
  isAdmin: false,
  refreshAdminFlag: async () => {
    const email = await whoami();
    set({ currentUserEmail: email, isAdmin: email !== null });
  },
  hydrateFromServer: async () => {
    const isAdmin = useLandmarkStore.getState().isAdmin;
    // Pull all endpoints in parallel. On failure (offline, etc.) we keep the
    // local cache so the UI still works. Admins read the full paper-frame
    // record (including unlocked drafts); everyone else reads the public one
    // which only returns the frame when it's locked.
    const [poisResult, idsResult, overridesResult, frameResult] = await Promise.allSettled([
      fetchAllPOIs(),
      fetchSelectionIds(),
      fetchLandmarkOverrides(),
      isAdmin ? fetchAdminPaperFrame() : fetchPublicPaperFrame(),
    ]);

    set((state) => {
      const patch: Partial<SelectionStore> = {};

      if (poisResult.status === 'fulfilled') {
        const customPOIs: Record<string, CustomPOI> = {};
        const customPOIImages: Record<string, string> = { ...state.customPOIImages };
        for (const { poi, heroImage } of poisResult.value) {
          customPOIs[poi.id] = poi;
          if (heroImage) customPOIImages[poi.id] = heroImage;
        }
        saveCustomPOIs(customPOIs);
        patch.customPOIs = customPOIs;
        patch.customPOIImages = customPOIImages;
      }

      if (idsResult.status === 'fulfilled') {
        // Server is the source of truth once we've fetched it. If the server
        // is empty AND we have a local seed, push our seed up (admin only).
        const serverIds = idsResult.value;
        if (serverIds.length === 0 && state.isAdmin && state.artistSelection.size > 0) {
          replaceSelection([...state.artistSelection]).catch(() => {
            /* tolerate; we'll retry on next toggle */
          });
        } else {
          const artistSelection = new Set(serverIds);
          saveArtistSelection(artistSelection);
          patch.artistSelection = artistSelection;
        }
      }

      if (overridesResult.status === 'fulfilled') {
        patch.landmarkOverrides = overridesResult.value;
      }

      if (frameResult.status === 'fulfilled') {
        const frame: ServerPaperFrame | null = frameResult.value;
        if (frame) {
          patch.paperFrameCenter = [frame.centerLng, frame.centerLat];
          patch.paperFrameShortSideMeters = frame.shortSideMeters;
          patch.paperFrameRotation = frame.rotationDeg;
          patch.paperFrameLocked = frame.locked;
          // Mirror to localStorage so a future offline load keeps the same
          // geometry. Opacity + border stay on the per-user preference.
          savePaperFrame({
            centerLng: frame.centerLng,
            centerLat: frame.centerLat,
            shortSideMeters: frame.shortSideMeters,
            rotationDeg: frame.rotationDeg,
            opacity: state.paperFrameOpacity,
            borderVisible: state.paperFrameBorderVisible,
          });
        } else {
          patch.paperFrameLocked = false;
        }
        patch.paperFrameServerLoaded = true;
      }

      return patch as Partial<SelectionStore> as SelectionStore;
    });
  },
  landmarkOverrides: {},
  saveLandmarkOverride: async (id, fields) => {
    const state = useLandmarkStore.getState();
    if (!state.isAdmin) {
      throw new Error('You need to sign in to edit landmarks.');
    }
    // Optimistic local update.
    const prev = state.landmarkOverrides[id];
    const prevFields = prev?.fields ?? {};
    const merged: Record<string, string> = { ...prevFields };
    for (const [k, v] of Object.entries(fields)) {
      if (v === null) delete merged[k];
      else merged[k] = v;
    }
    const optimistic: LandmarkOverride = {
      fields: merged,
      updatedAt: Date.now(),
      updatedBy: state.currentUserEmail ?? '',
    };
    const nextMap = Object.keys(merged).length === 0
      ? Object.fromEntries(Object.entries(state.landmarkOverrides).filter(([k]) => k !== id))
      : { ...state.landmarkOverrides, [id]: optimistic };
    set({ landmarkOverrides: nextMap });

    try {
      await patchLandmarkOverride(id, fields);
    } catch (err) {
      // Roll back on failure.
      const rollback = prev
        ? { ...state.landmarkOverrides, [id]: prev }
        : Object.fromEntries(
            Object.entries(state.landmarkOverrides).filter(([k]) => k !== id),
          );
      set({ landmarkOverrides: rollback });
      throw err;
    }
  },
  saveCustomPOI: async (poi, imageDataURL) => {
    if (!useLandmarkStore.getState().isAdmin) {
      throw new Error('You need to sign in to save POIs.');
    }
    // Image cache for the local UI (instant render after save).
    if (imageDataURL !== null) {
      // Best-effort IDB write; if it fails (private mode, quota) the image
      // still rides through the API + in-memory cache.
      void putCustomPOIImage(poi.id, imageDataURL).catch(() => undefined);
    }
    // Server write. The hero-image data URL goes up too so other browsers
    // see it; null means "don't change the existing image".
    await apiSavePOI(poi, imageDataURL);

    set((state) => {
      const customPOIs = { ...state.customPOIs, [poi.id]: poi };
      saveCustomPOIs(customPOIs);
      const customPOIImages =
        imageDataURL !== null
          ? { ...state.customPOIImages, [poi.id]: imageDataURL }
          : state.customPOIImages;
      const selectedAntwerpLandmark =
        state.selectedAntwerpLandmark?.id === poi.id ? poi : state.selectedAntwerpLandmark;
      return { customPOIs, customPOIImages, selectedAntwerpLandmark, editingPOI: null };
    });
  },
  deleteCustomPOI: async (id) => {
    if (!useLandmarkStore.getState().isAdmin) {
      throw new Error('You need to sign in to delete POIs.');
    }
    await apiDeletePOI(id);
    try {
      await deleteCustomPOIImage(id);
    } catch {
      /* tolerate missing image */
    }
    set((state) => {
      const customPOIs = { ...state.customPOIs };
      delete customPOIs[id];
      saveCustomPOIs(customPOIs);
      const customPOIImages = { ...state.customPOIImages };
      delete customPOIImages[id];
      const artistSelection = new Set(state.artistSelection);
      if (artistSelection.has(id)) {
        artistSelection.delete(id);
        saveArtistSelection(artistSelection);
      }
      const selectedAntwerpLandmark =
        state.selectedAntwerpLandmark?.id === id ? null : state.selectedAntwerpLandmark;
      return {
        customPOIs,
        customPOIImages,
        artistSelection,
        selectedAntwerpLandmark,
        editingPOI: null,
      };
    });
  },
  setActiveTab: (tab) => set({ activeTab: tab }),
  flyTo: (coordinates, zoom) => set({ flyToTarget: coordinates, flyToZoom: zoom ?? null }),
  consumeFlyToTarget: () => set({ flyToTarget: null, flyToZoom: null }),
  baseLayer: loadBaseLayer(),
  setBaseLayer: (id) => {
    saveBaseLayer(id);
    set({ baseLayer: id });
  },
  overlays: loadOverlayState(),
  toggleOverlay: (id) =>
    set((state) => {
      const next: OverlayStates = {
        ...state.overlays,
        [id]: { ...state.overlays[id], visible: !state.overlays[id].visible },
      };
      saveOverlayState(next);
      return { overlays: next };
    }),
  setOverlayOpacity: (id, opacity) =>
    set((state) => {
      const clamped = Math.max(0, Math.min(1, opacity));
      const next: OverlayStates = {
        ...state.overlays,
        [id]: { ...state.overlays[id], opacity: clamped },
      };
      saveOverlayState(next);
      return { overlays: next };
    }),
  currentYear: null,
  setYear: (year) =>
    set(() => {
      if (year === null) return { currentYear: null };
      const clamped = clampYear(year);
      const overlays = overlayStatesForYear(clamped);
      saveOverlayState(overlays);
      return { currentYear: clamped, overlays };
    }),
}));

export interface ParsedHash {
  /** Landmark slug (heritage records), if hash is `#landmark/{slug}`. */
  landmarkSlug: string | null;
  /** Business slug, if hash is `#business/{slug}`. */
  businessSlug: string | null;
  tab: SidePanelTab;
  isAdmin: boolean;
  /** Time-slider year if the hash carries `?year=YYYY`. */
  year: number | null;
}

/**
 * Parse `window.location.hash` into a structured shape.
 *
 * Patterns (the `path` and `query` are split on the first `?` after `#`):
 *   #                          → empty
 *   #landmark/{slug}           → landmark, about tab
 *   #landmark/{slug}/notes     → landmark, notes tab
 *   #business/{slug}           → business
 *   #admin                     → admin page
 *   #admin?token=...           → admin page with token
 *   #year=YYYY                 → time-slider only (no landmark)
 *   #landmark/X?year=YYYY      → landmark + time-slider (combined)
 */
export function parseHash(hash: string = typeof window === 'undefined' ? '' : window.location.hash): ParsedHash {
  const queryStart = hash.indexOf('?');
  const path = queryStart >= 0 ? hash.slice(0, queryStart) : hash;
  const query = queryStart >= 0 ? new URLSearchParams(hash.slice(queryStart + 1)) : null;

  // Year can appear in three forms:
  //   `#year=1780`              (year-only, no other path)
  //   `#landmark/X?year=1780`   (landmark + year, query-string style)
  //   `#business/X?year=1780`   (business + year, query-string style)
  let year: number | null = null;
  const yearOnlyMatch = /^#year=(-?\d+)$/.exec(path);
  if (yearOnlyMatch?.[1]) {
    year = parseYearParam(yearOnlyMatch[1]);
  } else {
    const yearRaw = query?.get('year');
    if (yearRaw !== null && yearRaw !== undefined) year = parseYearParam(yearRaw);
  }

  // If the path was a year-only hash, treat the rest as empty.
  const effectivePath = yearOnlyMatch ? '' : path;

  const adminMatch = /^#admin$/.exec(effectivePath);
  if (adminMatch) {
    return { landmarkSlug: null, businessSlug: null, tab: 'about', isAdmin: true, year };
  }

  const businessMatch = /^#business\/([\w-]+)$/.exec(effectivePath);
  if (businessMatch?.[1]) {
    return { landmarkSlug: null, businessSlug: businessMatch[1], tab: 'about', isAdmin: false, year };
  }

  const landmarkMatch = /^#landmark\/([\w-]+)(?:\/(notes|about))?$/.exec(effectivePath);
  if (landmarkMatch?.[1]) {
    const tab: SidePanelTab = landmarkMatch[2] === 'notes' ? 'notes' : 'about';
    return { landmarkSlug: landmarkMatch[1], businessSlug: null, tab, isAdmin: false, year };
  }

  return { landmarkSlug: null, businessSlug: null, tab: 'about', isAdmin: false, year };
}

function parseYearParam(raw: string): number | null {
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n)) return null;
  return clampYear(n);
}

export function writeHashForLandmark(slug: string | null, tab: SidePanelTab, year: number | null): void {
  const path = slug ? (tab === 'notes' ? `#landmark/${slug}/notes` : `#landmark/${slug}`) : '';
  applyHash(combine(path, year));
}

export function writeHashForBusiness(slug: string | null, year: number | null): void {
  applyHash(combine(slug ? `#business/${slug}` : '', year));
}

export function writeHashForYear(year: number | null): void {
  applyHash(year !== null ? `#year=${year}` : '');
}

function combine(path: string, year: number | null): string {
  if (year === null) return path;
  if (path === '') return `#year=${year}`;
  return `${path}?year=${year}`;
}

function applyHash(target: string): void {
  if (typeof window === 'undefined') return;
  const current = window.location.hash;
  if (target === current) return;
  if (target.length === 0 && current.length === 0) return;
  const url = target.length > 0 ? target : window.location.pathname + window.location.search;
  window.history.replaceState(null, '', url);
}

export function readAdminToken(): string | null {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash;
  if (!hash.startsWith('#admin')) return null;
  const queryStart = hash.indexOf('?');
  if (queryStart < 0) return null;
  const params = new URLSearchParams(hash.slice(queryStart + 1));
  return params.get('token');
}
